import { IStorage } from './storage.interface';
import { ContainerInfo } from './storage.container_info';
import { IPersistenceContainer } from '../../abstracts/persistence.container';
/**
 * This is an internal implementation of a storage container.  It takes a PersistenceContainer 
 * (which has a subset of the functionality) and straps on an info object to keep track of 
 * items that are added to the container.  This class can be used for creating storage
 * containers within other storage containers. 
 * 
 * @export
 * \@class PersistenceContainerImpl
 * 
 * @author Scott O'Bryan
 * \@since 1.0
 */
export class SubStorage implements IStorage {
private _info: ContainerInfo;
/**
 * Creates an instance of SubStorage.
 * @param {?} _namespace
 * @param {?} _root
 * @param {?=} _available
 */
constructor(private _namespace: string,
private _root: IPersistenceContainer,
private _available = true) {
        this._info = new ContainerInfo(_namespace, _root);
    }
/**
 * Sets a value
 * 
 * @param {?} key
 * @param {?} value
 * @return {?}
 */
public set(key: string, value: any): boolean {
        if (!this._available) {
            return false;
        }

        let /** @type {?} */ val = this._root.set(this._getNamespacedKey(key), value);
        this._info.addAttribute(key);
        return val;
    }
/**
 * Returns a value for a given key
 * 
 * @param {?} key
 * @return {?}
 */
public get(key: string): any {
        if (!this._available) {
            return undefined;
        }

        let /** @type {?} */ val = this._root.get(this._getNamespacedKey(key));

        if (val === undefined) {
            this._info.removeAttribute(key);
        }

        return val;
    }
/**
 * Removes a value for a given key
 * 
 * @param {?} key
 * @return {?}
 */
public remove(key: string): any {
        if (!this._available) {
            return undefined;
        }

        this._info.removeAttribute(key);
        return this._root.remove(this._getNamespacedKey(key));
    }
/**
 * Removes any values which have been stored using this subStorage
 * container.
 * @return {?}
 */
public removeAll(): void {
        this._info.getAttributes().forEach( (element) => { this.remove(element); });
    }
/**
 * Returns true if the parent storage object is available and if the
 * available flag was set durring instantiation
 * 
 * @return {?}
 */
public available(): boolean {
        return this._available && this._info.available();
    }
/**
 * Returns true if the value is not undefined
 * 
 * @param {?} key
 * @return {?}
 */
public exists(key: string): boolean {
        // This will also make sure the info object is up to date.
        return this.get(key) !== undefined;
    }
/**
 * Returns a list of un-namespaced keys that have been returned by this object.
 * 
 * @return {?}
 */
public keys(): string[] {
        // The exists will update the underlying storage object because
        // it invokes a get.  Still, we have an attributes object that is
        // not tied to the object in storage, hopefully.
        return this._info.getAttributes().filter((key) => {
            return this.exists(key);
        });
    }
/**
 * @param {?} key
 * @return {?}
 */
private _getNamespacedKey(key: string): string {
        return this._namespace + '::' + key;
    }
}

function SubStorage_tsickle_Closure_declarations() {
/** @type {?} */
SubStorage.prototype._info;
/** @type {?} */
SubStorage.prototype._namespace;
/** @type {?} */
SubStorage.prototype._root;
/** @type {?} */
SubStorage.prototype._available;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
